import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service } from '../service';
import { BatchGetThreadStatusIO } from '../types';

export const getBatchGetThreadStatusQueryKey = (req: BatchGetThreadStatusIO['input']) =>
  ['V4', 'BatchGetThreadStatus', req] as const;
type QueryKey = ReturnType<typeof getBatchGetThreadStatusQueryKey>;

/**
 * A hook that returns a query for the `BatchGetThreadStatus` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useBatchGetThreadStatusQuery = <E = unknown, D = BatchGetThreadStatusIO['output']>(
  req: BatchGetThreadStatusIO['input'],
  options?: LimitedSchemaQueryOptions<BatchGetThreadStatusIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<BatchGetThreadStatusIO['output'], E, D, QueryKey>({
    queryKey: getBatchGetThreadStatusQueryKey(req),
    queryFn: () => SchemaSMSDataV4Service.BatchGetThreadStatus(req, httpOptions),
    ...options,
    enabled: (options?.enabled ?? true) && Boolean(req?.requests?.length),
  });

/**
 * A hook that returns an array of queries for the `BatchGetThreadStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useBatchGetThreadStatusQueries = <E = unknown, D = BatchGetThreadStatusIO['output']>(
  argsArr: {
    req: BatchGetThreadStatusIO['input'];
    options?: LimitedSchemaQueryOptions<BatchGetThreadStatusIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getBatchGetThreadStatusQueryKey(req),
      queryFn: () => SchemaSMSDataV4Service.BatchGetThreadStatus(req, httpOptions),
      ...options,
      enabled: (options?.enabled ?? true) && Boolean(req?.requests?.length),
    }))
  ) as ReturnType<typeof useBatchGetThreadStatusQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `BatchGetThreadStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchBatchGetThreadStatus = () => {
  const queryClient = useQueryClient();

  const fetchBatchGetThreadStatus = async <E = unknown, D = BatchGetThreadStatusIO['output']>(
    req: BatchGetThreadStatusIO['input'],
    options?: LimitedSchemaFetchQueryOptions<BatchGetThreadStatusIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    if (!req?.requests?.length) {
      return { threadStatuses: [], invalidRequests: [] } as BatchGetThreadStatusIO['output'];
    }

    const response = await queryClient.fetchQuery<BatchGetThreadStatusIO['output'], E, D, QueryKey>(
      getBatchGetThreadStatusQueryKey(req),
      () => SchemaSMSDataV4Service.BatchGetThreadStatus(req, httpOptions),
      options
    );

    return response;
  };

  return fetchBatchGetThreadStatus;
};
